import { fetchDriverSeasonPointBreakdown } from '../championship/api.js';
import { buildSeasonPointsBreakdownTooltipHtml } from '@multirx/shared/game/seasonPointEvents.js';
import type {
  DriverSeasonPointsBreakdown,
  SeasonPointBucket,
  SeasonPointBreakdown,
} from '@multirx/shared/game/seasonPointEvents.js';
import {
  clearStandingsComboPoints,
  ensureStandingsComboProfileForRow,
  setStandingsComboPinned,
  setStandingsComboPointsHtml,
  standingsComboContains,
  __standingsTooltipTestState,
} from './standingsDriverTooltip.js';

const PILL_SELECTOR = '.standings-points-pill--tip[data-season-id][data-driver-id]';
const ROW_SELECTOR = '[data-driver-id][data-standings-rank]';

let tipAnchor: HTMLElement | null = null;
/** Pill sous la souris / focus (aperçu non épinglé). */
let hoveredPill: HTMLElement | null = null;
/** Pill dont le détail est verrouillé (clic) — permet de scroller. */
let pinnedPill: HTMLElement | null = null;
let showGeneration = 0;
let documentDismissBound = false;
const cache = new Map<string, Promise<DriverSeasonPointsBreakdown>>();
const boundContainers = new WeakSet<ParentNode>();

function cacheKey(seasonId: number, driverId: number): string {
  return `${seasonId}:${driverId}`;
}

function emptyBreakdown(driverId: number): SeasonPointBreakdown {
  return { driverId, total: 0, events: [] };
}

function emptyAll(driverId: number): DriverSeasonPointsBreakdown {
  return {
    driverId,
    championship: emptyBreakdown(driverId),
    qualification: emptyBreakdown(driverId),
    course: emptyBreakdown(driverId),
  };
}

function pickBucket(
  all: DriverSeasonPointsBreakdown,
  bucket: SeasonPointBucket
): SeasonPointBreakdown {
  if (bucket === 'qualification') return all.qualification;
  if (bucket === 'course') return all.course;
  return all.championship;
}

function parseBucket(raw: string | undefined): SeasonPointBucket {
  if (raw === 'qualification' || raw === 'course' || raw === 'championship') return raw;
  return 'championship';
}

function getBreakdown(seasonId: number, driverId: number): Promise<DriverSeasonPointsBreakdown> {
  const key = cacheKey(seasonId, driverId);
  let pending = cache.get(key);
  if (!pending) {
    pending = fetchDriverSeasonPointBreakdown(seasonId, driverId).catch(() => emptyAll(driverId));
    cache.set(key, pending);
  }
  return pending;
}

function setPillExpanded(pill: HTMLElement | null, expanded: boolean): void {
  if (!pill) return;
  pill.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function hidePointsPanel(): void {
  tipAnchor = null;
  clearStandingsComboPoints();
}

function activeAnchor(): HTMLElement | null {
  return pinnedPill ?? hoveredPill;
}

function showPoints(anchor: HTMLElement, html: string, generation: number): void {
  if (generation !== showGeneration || activeAnchor() !== anchor) return;
  tipAnchor = anchor;
  const row = anchor.closest<HTMLElement>(ROW_SELECTOR);
  if (row) {
    ensureStandingsComboProfileForRow(row);
  }
  setStandingsComboPointsHtml(html);
  setStandingsComboPinned(pinnedPill != null);
}

function scheduleTip(pill: HTMLElement): void {
  const seasonId = parseInt(pill.dataset.seasonId ?? '', 10);
  const driverId = parseInt(pill.dataset.driverId ?? '', 10);
  const bucket = parseBucket(pill.dataset.bucket);
  if (!Number.isFinite(seasonId) || !Number.isFinite(driverId)) return;

  ensureDocumentDismiss();
  hoveredPill = pill;
  const row = pill.closest<HTMLElement>(ROW_SELECTOR);
  if (row) ensureStandingsComboProfileForRow(row);

  const generation = ++showGeneration;
  void getBreakdown(seasonId, driverId).then((all) => {
    if (generation !== showGeneration || activeAnchor() !== pill) return;
    const breakdown = pickBucket(all, bucket);
    showPoints(pill, buildSeasonPointsBreakdownTooltipHtml(breakdown, bucket), generation);
  });
}

function unpin(): void {
  const prev = pinnedPill;
  pinnedPill = null;
  setPillExpanded(prev, false);
  setStandingsComboPinned(false);
}

function dismiss(): void {
  unpin();
  hoveredPill = null;
  showGeneration += 1;
  hidePointsPanel();
}

function pinPill(pill: HTMLElement): void {
  if (pinnedPill && pinnedPill !== pill) {
    setPillExpanded(pinnedPill, false);
  }
  pinnedPill = pill;
  hoveredPill = pill;
  setPillExpanded(pill, true);
  setStandingsComboPinned(true);
  ensureDocumentDismiss();
  scheduleTip(pill);
}

function togglePin(pill: HTMLElement): void {
  if (pinnedPill === pill) {
    dismiss();
    return;
  }
  pinPill(pill);
}

function onDocumentPointerDown(e: Event): void {
  if (!pinnedPill) return;
  const target = e.target;
  if (!(target instanceof Node)) return;
  if (pinnedPill.contains(target)) return;
  if (standingsComboContains(target)) return;
  dismiss();
}

function onDocumentKeyDown(e: Event): void {
  if (!(e instanceof KeyboardEvent)) return;
  if (e.key !== 'Escape') return;
  if (!pinnedPill) return;
  dismiss();
}

function ensureDocumentDismiss(): void {
  if (documentDismissBound) return;
  documentDismissBound = true;
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onDocumentKeyDown, true);
}

function teardownDocumentDismiss(): void {
  if (!documentDismissBound) return;
  documentDismissBound = false;
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  document.removeEventListener('keydown', onDocumentKeyDown, true);
}

function isEntering(e: MouseEvent, el: HTMLElement): boolean {
  const from = e.relatedTarget;
  return !(from instanceof Node) || !el.contains(from);
}

function isLeaving(e: MouseEvent, el: HTMLElement): boolean {
  const to = e.relatedTarget;
  return !(to instanceof Node) || !el.contains(to);
}

/** Survol / focus / clic des pills de points → détail docké à droite du profil. */
export function bindStandingsPointsBreakdownTooltips(container: ParentNode): void {
  if (boundContainers.has(container)) return;
  boundContainers.add(container);
  ensureDocumentDismiss();

  container.addEventListener('mouseover', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !container.contains(pill)) return;
    if (!isEntering(e as MouseEvent, pill)) return;
    if (pinnedPill && pinnedPill !== pill) return;
    e.stopPropagation();
    scheduleTip(pill);
  });

  container.addEventListener('mouseout', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !isLeaving(e as MouseEvent, pill)) return;
    if (pinnedPill === pill) return;
    const to = (e as MouseEvent).relatedTarget;
    if (to instanceof Node && standingsComboContains(to)) return;
    if (hoveredPill === pill) hoveredPill = null;
    showGeneration += 1;
    if (tipAnchor === pill) hidePointsPanel();
  });

  container.addEventListener('focusin', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !container.contains(pill)) return;
    if (pinnedPill && pinnedPill !== pill) return;
    e.stopPropagation();
    scheduleTip(pill);
  });

  container.addEventListener('focusout', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !isLeaving(e as MouseEvent, pill)) return;
    if (pinnedPill === pill) return;
    if (hoveredPill === pill) hoveredPill = null;
    showGeneration += 1;
    if (tipAnchor === pill) hidePointsPanel();
  });

  container.addEventListener('click', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !container.contains(pill)) return;
    e.preventDefault();
    e.stopPropagation();
    togglePin(pill);
  });

  container.addEventListener('keydown', (e) => {
    if (!(e instanceof KeyboardEvent)) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (!(e.target instanceof HTMLElement)) return;
    const pill = e.target.closest<HTMLElement>(PILL_SELECTOR);
    if (!pill || !container.contains(pill)) return;
    e.preventDefault();
    e.stopPropagation();
    togglePin(pill);
  });
}

export function invalidateStandingsPointsBreakdownCache(): void {
  cache.clear();
}

export function removeStandingsPointsBreakdownTooltip(): void {
  dismiss();
  cache.clear();
}

/** @internal Tests */
export function __standingsPointsTipTestState(): {
  hoveredPill: HTMLElement | null;
  pinnedPill: HTMLElement | null;
  showGeneration: number;
  isVisible: boolean;
  isPinned: boolean;
  tipEl: HTMLDivElement | null;
} {
  const combo = __standingsTooltipTestState();
  return {
    hoveredPill,
    pinnedPill,
    showGeneration,
    isVisible: combo.hasPointsPanel,
    isPinned: pinnedPill != null,
    tipEl: combo.comboEl,
  };
}

/** @internal Tests */
export function __resetStandingsPointsTipTestState(): void {
  removeStandingsPointsBreakdownTooltip();
  teardownDocumentDismiss();
}
